import LaunchIcon from '@mui/icons-material/Launch';
import { Box, Divider, FormControlLabel, FormHelperText, Link, Stack, Switch } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FlowEntityType, FlowExportMode } from 'flow-schema';
import _ from 'lodash';
import { useCallback, useId, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { FormField, Text, api, colors, getQueryProps, isDefined, isTruthy } from 'common';
import { type FlowDefinition } from 'vivaa-api';

import { DynamicParamsEditor } from './DynamicParamsEditor/DynamicParamsEditor';
import { SchedulingConfigsFormFields } from './SchedulingConfigs/SchedulingConfigs';
import { FormTemplateDetails } from './TemplateDetails';
import { NotificationSchemeAutocomplete } from '../../../../../../PrevisitFormTemplate/BaseView/NotificationSchemeAutocomplete';
import { useFlowDefinitionContext } from '../../../../context';
import { type SearchBarOption } from '../../../../types';
import { useReactFlowViewContext } from '../../../context/context';
import { BlockValueTemplateField } from '../../ConfigFields/CodeMirror/FlowTemplate.tsx';
import { BlockValueTemplateFieldWrapper } from '../../ConfigFields/CodeMirror/FlowTemplateFieldWrapper';
import { BlockKeyValuesField } from '../../ConfigFields/KeyValues/KeyValuesField';
import { BlockRunModeField } from '../../ConfigFields/RunMode';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { BlockSwitchField } from '../../ConfigFields/Switch';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';
import { NodeSearchBar } from '../../components/NodeSearchBar';

type FormBlock = FlowDefinition.FormBlock;

interface FormBlockFormFieldsProps {
  mode?: 'v1' | 'v2';
}

export function FormBlockFormFields(props: FormBlockFormFieldsProps = {}) {
  const { mode = 'v1' } = props;
  const { isEditable } = useReactFlowViewContext();
  const {
    flowDefinition: { organizationId },
  } = useFlowDefinitionContext();
  const { value: formTemplateConfigs, setValue } = useSelectedNodeField<
    FormBlock['config']['formTemplateConfigs']
  >('config.formTemplateConfigs');

  const { value: isOverridingEntity, setValue: setIsOverridingEntity } =
    useSelectedNodeField<boolean>('config.isOverridingEntity');

  const { value: entityTypeValue, setValue: setEntityTypeValue } =
    useSelectedNodeField<FlowEntityType | null>('config.entityOverride.entityType');

  const { setValue: setEntityIdValue } = useSelectedNodeField<string>(
    'config.entityOverride.entityId'
  );

  const {
    value: notificationSchemeIdOverride,
    setValue: setNotificationSchemeIdOverride,
    visibleError: notificationSchemeVisibleError,
  } = useSelectedNodeField<string | null>('config.notificationSchemeIdOverride');
  const [isOverridingScheme, setIsOverridingScheme] = useState(
    isDefined(notificationSchemeIdOverride)
  );
  const notificationSchemeFieldId = useId();

  const lookupSchemeId =
    isDefined(notificationSchemeIdOverride) && !notificationSchemeIdOverride.includes('{{')
      ? notificationSchemeIdOverride
      : undefined;
  const { data: notificationScheme, isError: isNotificationSchemeError } = useQuery({
    ...getQueryProps(api.client.NotificationScheme, 'findOneById', lookupSchemeId ?? 'INVALID_ID'),
    enabled: isTruthy(lookupSchemeId),
  });
  const requiredVariables = api.NotificationTemplate.findSchemeCustomVariables(notificationScheme);

  const { data: formTemplates } = useQuery({
    ...getQueryProps(api.client.Previsit, 'fetchPrevisitFormTemplates', { organizationId }),
    select: (data) => data.previsitFormTemplates,
  });
  const [displayTemplateOptions, setDisplayTemplateOptions] = useState<SearchBarOption[]>([]);
  const onDenouncedTemplateSearchInput = useCallback(
    (input: string) => {
      setDisplayTemplateOptions(
        (
          formTemplates?.filter((template) =>
            template.name.toLowerCase().includes(input.toLowerCase())
          ) || []
        ).map((template) => ({ id: template.id, name: template.name }))
      );
    },
    [formTemplates]
  );

  // Create dropdown options for FlowEntityType
  const entityTypeOptions = Object.values(FlowEntityType).map((entityType: FlowEntityType) => ({
    value: entityType,
    label: _.capitalize(entityType.replace(/([A-Z])/g, ' $1')),
  }));

  return (
    <Stack gap={1}>
      <Stack gap={2}>
        <Stack gap={0.5}>
          <NodeSearchBar
            label="Add form"
            onSelect={(id) => {
              if (formTemplateConfigs?.every((config) => config.formTemplateId !== id)) {
                setValue([...formTemplateConfigs, { formTemplateId: id }]);
              }
            }}
            onDebouncedInputEffect={onDenouncedTemplateSearchInput}
            options={displayTemplateOptions}
            disabled={!isEditable}
            placeholder="Search from existing forms"
          />
          <Text variant="body2" color={colors.extended.gray['45']}>
            Select from existing forms or{' '}
            <Link
              component={ReactRouterLink}
              to="/previsit-form-templates"
              color="inherit"
              rel="noopener noreferrer"
              target="_blank"
            >
              create a new form.
            </Link>
          </Text>
        </Stack>
        {formTemplateConfigs?.map(({ formTemplateId }, index) => (
          <Stack key={formTemplateId} direction="row" gap={1}>
            <FormTemplateDetails
              templateName={
                formTemplates?.find((template) => template.id === formTemplateId)?.name || 'Unknown'
              }
              templateId={formTemplateId}
              fieldNamePrefix={`config.formTemplateConfigs.${index}`}
              onRemove={() => {
                setValue(formTemplateConfigs.filter((_, i) => i !== index));
              }}
            />
          </Stack>
        ))}
      </Stack>
      <Divider sx={{ marginY: 1.5 }} />
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Text variant="heading5" bold alignContent="center">
          Override Entity
        </Text>
        <Box>
          <BlockSwitchField
            /**
             * FIXME: The empty label here is an anti-pattern that is being used begrudgingly to conform to existing design.
             *  BlockSwitchField label must include the label that tells the user what the switch does.
             *  Without this, the switch fails accessibility standards.
             *  It also aligns with our E2E testing utilities, since the test would click on the switch based on its label.
             *  In addition, the `BlockSwitchField` should be consistently used in our UI and designs.
             */
            label=""
            fieldName="config.isOverridingEntity"
            onChange={() => {
              setIsOverridingEntity(!isOverridingEntity);
              setEntityTypeValue(null);
              setEntityIdValue('');
            }}
          />
        </Box>
      </Stack>
      {!isOverridingEntity && (
        <FormHelperText>
          Not overriding entity: flow will use the entity that starts the flow
        </FormHelperText>
      )}
      {isOverridingEntity && (
        <>
          <FormHelperText>
            Overriding the entity: flow will use the entity specified here
          </FormHelperText>
          <BlockSelectField
            fieldName="config.entityOverride.entityType"
            label="Entity Type"
            helperText="Select the type of entity to override"
            options={entityTypeOptions}
            value={entityTypeValue || ''}
            displayEmpty
            required
          />
          <BlockValueTemplateField label="Entity Id" fieldName="config.entityOverride.entityId" />
        </>
      )}

      {mode === 'v2' && (
        <>
          <Divider sx={{ marginY: 1.5 }} />
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Text variant="heading5" bold alignContent="center">
              Override Notification Scheme
            </Text>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={isOverridingScheme}
                    disabled={!isEditable}
                    onChange={(_event, isChecked) => {
                      setIsOverridingScheme(isChecked);
                      if (!isChecked) {
                        setNotificationSchemeIdOverride(null);
                      }
                    }}
                    slotProps={{ input: { 'aria-label': 'Override notification scheme' } }}
                  />
                }
                label=""
              />
            </Box>
          </Stack>
          {!isOverridingScheme && (
            <FormHelperText>
              Not overriding: each form uses its template&apos;s configured notification scheme.
            </FormHelperText>
          )}
          {isOverridingScheme && (
            <Stack gap={1}>
              <FormHelperText>
                Overriding: When set, this notification scheme will be used for all forms added by
                this block.
              </FormHelperText>
              <FormField
                id={notificationSchemeFieldId}
                label="Notification scheme"
                error={
                  notificationSchemeVisibleError ??
                  (isNotificationSchemeError
                    ? 'Failed to load the notification scheme. Its required variables may not appear.'
                    : undefined)
                }
              >
                <BlockValueTemplateFieldWrapper
                  fieldId={notificationSchemeFieldId}
                  fieldName="config.notificationSchemeIdOverride"
                  aria-label="Notification scheme"
                >
                  <NotificationSchemeAutocomplete
                    id={notificationSchemeFieldId}
                    schemeId={
                      notificationSchemeIdOverride?.includes('{{')
                        ? null
                        : (notificationSchemeIdOverride ?? null)
                    }
                    onChange={(schemeId) => setNotificationSchemeIdOverride(schemeId)}
                    placeholder="Select a notification scheme"
                    disabled={!isEditable}
                    organizationId={organizationId}
                    variant="borderless"
                  />
                </BlockValueTemplateFieldWrapper>
              </FormField>
              <Box>
                <Link
                  href="#/notifications/scheme"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  underline="hover"
                  aria-label="Open notification schemes in a new tab"
                >
                  Manage schemes
                  <LaunchIcon sx={{ fontSize: 12, marginLeft: '2px', verticalAlign: 'middle' }} />
                </Link>
              </Box>
            </Stack>
          )}
        </>
      )}

      <Divider sx={{ marginY: 1.5 }} />
      {mode === 'v2' ? (
        <DynamicParamsEditor requiredVariables={requiredVariables} />
      ) : (
        <BlockKeyValuesField
          fieldName="config.dynamicParams"
          label="Dynamic Parameters"
          helperText="Enter Form Dynamic Parameters."
        />
      )}

      <Divider sx={{ marginY: 1.5 }} />
      <SchedulingConfigsFormFields fieldNamePrefix="config.schedulingConfigs" />
      <Divider sx={{ marginY: 1.5 }} />
      <BlockSwitchField
        label="Run Auto Export"
        fieldName="config.exportMode"
        toggleStates={{
          activeState: FlowExportMode.RunAutoExport,
          inactiveState: FlowExportMode.NoAutoExport,
        }}
        defaultState={FlowExportMode.RunAutoExport}
        helperText="Executes default exports (export forms, export patient information, etc.) on form completion."
      />
      {mode === 'v1' && (
        <>
          <Divider sx={{ marginY: 1.5 }} />
          <BlockRunModeField
            fieldName="config.runMode"
            label="Test Mode"
            helperText="Runs this flow without executing the associated form set."
          />
        </>
      )}
    </Stack>
  );
}
